import { useState, useEffect } from "react";
import { Modal, Form, InputGroup } from "react-bootstrap";
import { Upload, Plus, X } from "lucide-react";
import { FaSearch } from "react-icons/fa";
import ImageCard from "./ImageCard";
import localization from "services/localization";
import { getResourceType, getResourceIcon } from "services/utils";
import ResourcePreview from "./ResourcePreview";
import DeleteModal from "../DeleteModal";
import SpecificAPI from "services/SpecificApi";
import { useUserStore } from "context/useUserStore";
import { toast } from "react-toastify";

export default function ImageDashboard({
  images = [],
  onSelectImage,
  onUploadClick,
  showAdd = true,
  setImages,
  loading,
  setLoading,
  selectedImage,
  onRemoveSelected,
  pendingImage,
}) {
  const [showViewModal, setShowViewModal] = useState(false);
  const [itemToView, setItemToView] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemName, setItemName] = useState("");
  const { appUser, userData } = useUserStore();

  useEffect(() => {
    filterResources();
  }, [searchTerm, images]);

  const filterResources = () => {
    let allItems = [...images];

    if (pendingImage) {
      const pendingDisplay = {
        id: "pending",
        originalName: pendingImage.get("files[]")?.name || "Pending Image",
        signedUrl: URL.createObjectURL(pendingImage.get("files[]")),
        apiUrl: URL.createObjectURL(pendingImage.get("files[]")),
        type: "image",
      };
      allItems = [pendingDisplay, ...allItems];
    }

    if (!searchTerm) {
      setFilteredItems(allItems);
    } else {
      const filtered = allItems.filter((res) =>
        res.originalName.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredItems(filtered);
    }
  };

  const handleView = (item) => {
    setItemToView(item);
    setShowViewModal(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setItemName(item.originalName || "");
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      await SpecificAPI.updateResources(
        userData?.selected_school_id,
        itemToDelete.id,
        { enabled: false },
      );

      setImages((prev) => prev.filter((ev) => ev.id !== itemToDelete.id));

      toast.success("Resource deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete resource. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setLoading(false);
    }
  };

  return (
    <div className="resource-library">
      <div className="flex justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold">
            {localization.resource_library || "Resource Library"}
          </h1>
          <p className="description_text">
            {localization.browse_manage_resources ||
              "Browse and manage your resources"}
          </p>
        </div>
        {showAdd && (
          <button
            onClick={onUploadClick}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg"
          >
            <Plus size={18} />
            {localization.add_resource || "Add Resource"}
          </button>
        )}
      </div>

      {/* Search Filter */}
      <div className="d-flex align-items-center gap-3 w-100 mt-4 mb-4">
        <InputGroup className="search-field flex-grow-1">
          <InputGroup.Text id="search-addon" className="customSearchAddon">
            <FaSearch className="icon-btn" />
          </InputGroup.Text>
          <Form.Control
            placeholder={
              localization.search_module_placeholder ||
              "Search by resource name..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="customSearchInput"
          />
        </InputGroup>
      </div>

      {/* Resource Grid */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {" "}
            {filteredItems.length > 0 ? (
              filteredItems.map((res) => {
                const type = getResourceType(res);
                const icon = getResourceIcon(type);
                const url = res.signedUrl || res.gcs?.public_url;
                const apiUrl = res.apiUrl || url;

                return (
                  <ImageCard
                    key={res.id}
                    image={{
                      ...res,
                      type,
                      icon,
                      signedUrl: url,
                      name: res.originalName,
                    }}
                    isSelected={selectedImage === apiUrl}
                    onSelect={() => onSelectImage(apiUrl)}
                    onView={handleView}
                    hasDelete={false}
                  />
                );
              })
            ) : (
              <div className="text-center py-16 col-span-12">
                <Upload size={40} className="mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600">
                  {localization.no_resources_yet || "No resources yet"}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar for Selected */}
        {/* {selectedImage !== null && (
          <div className="col-span-4 resource-sidebar rounded-xl shadow p-4 flex flex-col">
            <h2 className="font-bold text-lg mb-4">
              {localization.selected || "Selected"}
            </h2>
            <div className="flex-1 space-y-3 overflow-y-auto">
              {(() => {
                const res = images.find((r) => r.id === selectedImage);
                if (!res) return null;

                return (
                  <div
                    key={res.id}
                    className="resource-card flex justify-between items-center"
                  >
                    <div className="min-w-0">
                      <p className="resource-name">{res.originalName}</p>
                      <p className="text-xs text-slate-500">
                        {getResourceType(res)}
                      </p>
                    </div>
                    <button
                      onClick={() => onRemoveSelected(res.id)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X size={16} />
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        )} */}
      </div>

      {/* View Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        centered
        size="xl"
        backdrop="static"
        className="themed-modal addEditModal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {itemToView?.originalName || "View Resource"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "90vh", overflowY: "auto" }}>
          {itemToView && (
            <div className="w-full h-full flex items-center justify-center">
              {itemToView.type === "image" ? (
                <img
                  src={itemToView.signedUrl || "/placeholder.svg"}
                  alt={itemToView.originalName}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              ) : (
                <ResourcePreview resource={itemToView} />
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        itemName={itemName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
}
